import type { ScheduleRecipient } from "@common-reducers/AddScheduledMessageButtonReducer";

/**
 * Editing a multi-recipient schedule must UPDATE the documents that already exist for
 * those recipients, never re-create them.
 *
 * A multi-recipient compose is persisted as one message per recipient (FE#204/FE#230).
 * The dialog opened on one of those documents is seeded with ONLY that document's recipient
 * (AddScheduledMessageButtonReducer.ts deriveOpenRecipients), so the user rebuilds a
 * multi-recipient selection by adding recipients, some of which already have a sibling
 * document. The edit path used to PATCH the first recipient and CREATE every other one,
 * which left the existing sibling documents in place and added a second copy of each → the
 * same group was sent the same message several times.
 *
 * A "sibling" is a live, pending message for the same recipient that still matches the
 * message as it was BEFORE this edit (same due date, text, attachment and poll question).
 * Matching on the pre-edit values is what makes it a sibling of the edited document
 * rather than an unrelated schedule to the same group. Recurrence is part of the match,
 * so a one-off and a recurring message are never siblings.
 *
 * Siblings are looked up in the client store only: one that is not loaded falls back to
 * create, which is no worse than the old behaviour.
 */

export type EditFanOutMessage = {
  _id: string;
  status?: string;
  deleted?: boolean;
  dueDate?: Date | string | number;
  message?: string;
  asset?: { url?: string } | null;
  poll?: { question?: string } | null;
  isRecurring?: boolean;
  rruleset?: string | null;
  contactList?: ReadonlyArray<{ id?: string }> | null;
};

export type EditFanOutStep =
  | { contact: ScheduleRecipient; action: "patch"; _id: string }
  | { contact: ScheduleRecipient; action: "create" };

const recipientId = (m: EditFanOutMessage): string | undefined => m.contactList?.[0]?.id;

const sameSlot = (a: EditFanOutMessage, b: EditFanOutMessage): boolean =>
  new Date(a.dueDate ?? NaN).getTime() === new Date(b.dueDate ?? NaN).getTime() &&
  (a.message ?? "") === (b.message ?? "") &&
  (a.asset?.url ?? "") === (b.asset?.url ?? "") &&
  (a.poll?.question ?? "") === (b.poll?.question ?? "") &&
  !!a.isRecurring === !!b.isRecurring &&
  (a.rruleset ?? "") === (b.rruleset ?? "");

export function planEditFanOut(
  contactList: readonly ScheduleRecipient[],
  editedId: string,
  storedMessages: readonly EditFanOutMessage[],
): EditFanOutStep[] {
  const original = storedMessages.find((m) => m._id === editedId);
  const editedRecipient = original ? recipientId(original) : undefined;

  // Live siblings of the edited document, by recipient (oldest _id wins if the store
  // already holds duplicates, so the surviving one is the original).
  const siblingByRecipient = new Map<string, string>();
  if (original) {
    const live = storedMessages
      .filter((m) => m._id !== editedId && !m.deleted && m.status === "pending" && sameSlot(m, original))
      .sort((a, b) => (a._id < b._id ? -1 : a._id > b._id ? 1 : 0));
    for (const m of live) {
      const rid = recipientId(m);
      if (rid && !siblingByRecipient.has(rid)) siblingByRecipient.set(rid, m._id);
    }
  }

  // The edited document keeps its own recipient when it is still selected; if the user
  // swapped it out, it is re-pointed at the first selection that has no sibling document.
  const editedKeeps = contactList.find((c) => c.id === editedRecipient);
  const editedTakes =
    editedKeeps ?? contactList.find((c) => !(c.id && siblingByRecipient.has(c.id)));

  return contactList.map((contact): EditFanOutStep => {
    if (contact === editedTakes) return { contact, action: "patch", _id: editedId };
    const siblingId = contact.id ? siblingByRecipient.get(contact.id) : undefined;
    return siblingId ? { contact, action: "patch", _id: siblingId } : { contact, action: "create" };
  });
}

/**
 * The edited document when the plan leaves it untouched: the user removed its recipient and
 * every recipient they selected already has a sibling. Left alone it would still send the old
 * text to the removed recipient, so the caller deletes it once the fan-out has been saved.
 */
export function editedDocLeftBehind(plan: readonly EditFanOutStep[], editedId: string): string | undefined {
  return plan.some((step) => step.action === "patch" && step._id === editedId) ? undefined : editedId;
}
